import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
  IconButton,
  Chip,
  Grid
} from '@mui/material';
import { 
    Refresh, FilterList, Event, Person, NetworkCheck, 
    Code, Info, ChevronRight, ChevronLeft, Close 
} from '@mui/icons-material';
import api from '../../utils/api';
import { snackbar } from '../../utils/showMessage';
import JalaliDatePicker from '../../components/JalaliDatePicker';

const formatJalaliDisplay = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('fa-IR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
};

const textFieldStyle = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
  },
};

const initialSummary = {
  total: 0,
  views: 0,
  errors: 0,
  uniqueUsers: 0,
  topResource: '-',
  topAction: '-',
};

const computeSummary = (logs) => {
  const summary = { ...initialSummary, total: logs.length };
  const actionCounts = {};
  const resourceCounts = {};
  const userSet = new Set();

  logs.forEach((log) => {
    if (log.action === 'view') summary.views += 1;
    if (log.success === false) summary.errors += 1;
    if (log.action) actionCounts[log.action] = (actionCounts[log.action] || 0) + 1;
    if (log.resource) resourceCounts[log.resource] = (resourceCounts[log.resource] || 0) + 1;
    const identifier = log.userId || log.phone;
    if (identifier) {
      userSet.add(identifier);
    }
  });

  const topEntry = (map) => {
    const records = Object.entries(map);
    if (!records.length) return '-';
    return records.sort((a, b) => b[1] - a[1])[0][0];
  };

  summary.topAction = topEntry(actionCounts);
  summary.topResource = topEntry(resourceCounts);
  summary.uniqueUsers = userSet.size;
  return summary;
};

const formatDateRange = (from, to) => {
  if (!from && !to) return 'بدون محدوده';
  const parts = [];
  if (from) parts.push(`از ${formatJalaliDisplay(from) || from}`);
  if (to) parts.push(`تا ${formatJalaliDisplay(to) || to}`);
  return parts.join(' — ');
};

const AuditLogs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(initialSummary);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    action: '',
    resource: '',
    method: '',
    ip: '',
    userId: '',
    from: '',
    to: '',
  });
  const [selected, setSelected] = useState(null);

  const loadItems = async (overridePage, overrideFilters) => {
    try {
      setLoading(true);
      const params = {
        page: overridePage || page,
        limit: 20,
        ...(overrideFilters || filters),
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      const res = await api.get('/admin/audit-logs', { params });
      const fetched = res?.data?.items || [];
      setItems(fetched);
      setSummary(computeSummary(fetched));
      setTotal(res?.data?.total || 0);
      setTotalPages(res?.data?.totalPages || 1);
    } catch (e) {
      console.error(e);
      snackbar('خطا در بارگذاری لاگ‌ها');
      setItems([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [page]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleDateChange = (name) => (iso) => {
    setFilters({ ...filters, [name]: iso });
  };

  const applyFilters = () => {
    setPage(1);
    loadItems(1, filters);
  };

  const clearFilters = () => {
    const cleared = { action: '', resource: '', method: '', ip: '', userId: '', from: '', to: '' };
    setFilters(cleared);
    setPage(1);
    loadItems(1, cleared);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 5, mb: 5, pt: isMobile ? 8 : 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="primary">لاگ‌های مدیریتی</Typography>
        <Box display="flex" gap={1}>
          <IconButton 
            onClick={() => setShowFilters(!showFilters)} 
            color={showFilters ? "primary" : "default"}
            sx={{ bgcolor: showFilters ? 'primary.lighter' : 'grey.100' }}
          >
            <FilterList fontSize="small" />
          </IconButton>
          <IconButton 
            onClick={() => loadItems()} 
            disabled={loading}
            sx={{ bgcolor: 'grey.100' }}
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {showFilters && (
        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', p: 3, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom mb={2}>فیلترها</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth name="action" label="عملیات (create/update/delete)" value={filters.action} onChange={handleFilterChange} sx={textFieldStyle} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth name="resource" label="منبع (resource)" value={filters.resource} onChange={handleFilterChange} sx={textFieldStyle} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth name="method" label="متد (POST/PUT/DELETE)" value={filters.method} onChange={handleFilterChange} sx={textFieldStyle} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth name="ip" label="IP" value={filters.ip} onChange={handleFilterChange} sx={textFieldStyle} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth name="userId" label="شناسه کاربر" value={filters.userId} onChange={handleFilterChange} sx={textFieldStyle} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <JalaliDatePicker
                label="از تاریخ"
                value={filters.from}
                onChange={handleDateChange('from')}
                sx={textFieldStyle}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <JalaliDatePicker
                label="تا تاریخ"
                value={filters.to}
                onChange={handleDateChange('to')}
                endOfDay
                sx={textFieldStyle}
              />
            </Grid>
          </Grid>
          <Box mt={2} display="flex" gap={1}>
            <Button variant="contained" onClick={applyFilters} sx={{ borderRadius: 2 }}>اعمال فیلتر</Button>
            <Button variant="text" onClick={clearFilters} sx={{ borderRadius: 2 }}>پاک کردن فیلتر</Button>
          </Box>
        </Paper>
      )}

      <Grid container spacing={2} mb={3} alignItems="stretch">
        {[{
          label: 'کل درخواست‌ها',
          value: summary.total,
          help: 'شامل GET و تغییر‌دهنده‌ها'
        }, {
          label: 'بازدیدهای صفحه',
          value: summary.views,
          help: 'اکشن view برای ورود به صفحات'
        }, {
          label: 'خطاها',
          value: summary.errors,
          help: 'درخواست‌های ناموفق'
        }, {
          label: 'کاربران یونیک',
          value: summary.uniqueUsers,
          help: 'کاربرانی که لاگ تولید کرده‌اند'
        }].map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Paper sx={{ borderRadius: 3, p: 2, minHeight: 110 }} elevation={2}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>{card.label}</Typography>
              <Typography variant="h5" fontWeight="bold">{card.value}</Typography>
              <Typography variant="caption" color="text.secondary">{card.help}</Typography>
            </Paper>
          </Grid>
        ))}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ borderRadius: 3, p: 2, minHeight: 110 }} elevation={2}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>بازه تاریخ</Typography>
            <Typography variant="h6" fontWeight="bold">{formatDateRange(filters.from, filters.to)}</Typography>
            <Typography variant="caption" color="text.secondary">{summary.topAction !== '-' ? `پرکاربردترین اکشن: ${summary.topAction}` : 'بدون اکشن برجسته'}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ borderRadius: 3, p: 2, minHeight: 110 }} elevation={2}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>منبع پرتکرار</Typography>
            <Typography variant="h6" fontWeight="bold">{summary.topResource}</Typography>
            <Typography variant="caption" color="text.secondary">{summary.topResource === '-' ? 'تنظیمی ندارد' : 'بیشترین لاگ‌ها از این منبع'}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.map((it) => (
            <Card key={it._id || `${it.path}-${it.createdAt}`} 
              sx={{ 
                borderRadius: 4, 
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)', 
                borderLeft: it.success === false ? '5px solid red' : '5px solid transparent'
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1.5}>
                   <Box display="flex" alignItems="center" gap={0.5}>
                      <Chip 
                        label={it.action} 
                        size="small" 
                        color={it.action === 'delete' ? 'error' : it.action === 'create' ? 'success' : 'info'} 
                        sx={{ borderRadius: 1.5, fontWeight: 'bold' }}
                      />
                      <Chip 
                        label={it.method} 
                        size="small" 
                        variant="outlined"
                        sx={{ borderRadius: 1.5 }}
                      />
                   </Box>
                   <Typography variant="caption" color="text.secondary">
                      {it.createdAt ? new Date(it.createdAt).toLocaleString('fa-IR') : '-'}
                   </Typography>
                </Box>
                
                <Box display="flex" flexDirection="column" gap={1}>
                   <Typography variant="body2">
                      <strong>منبع:</strong> {it.resource}
                   </Typography>
                   <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      <strong>مسیر:</strong> {it.path}
                   </Typography>
                   <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption">{it.phone || it.userId || '-'}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <NetworkCheck sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption">{it.ip || '-'}</Typography>
                      </Box>
                   </Box>
                </Box>

                <Divider sx={{ my: 1.5, borderStyle: 'dotted' }} />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                   <Chip 
                      label={it.statusCode || (it.success === false ? 'خطا' : '200')} 
                      size="small" 
                      sx={{ bgcolor: it.success === false ? 'error.lighter' : 'success.lighter', color: it.success === false ? 'error.dark' : 'success.dark' }}
                   />
                   <Button size="small" startIcon={<Info />} onClick={() => setSelected(it)}>جزئیات</Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>زمان</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>کاربر</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>IP</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>عملیات</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>منبع / مسیر</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>وضعیت</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>جزئیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it._id || `${it.path}-${it.createdAt}`} hover
                  sx={{ backgroundColor: it.success === false ? 'rgba(255, 59, 48, 0.04)' : 'transparent' }}>
                  <TableCell align="right" sx={{ py: 1.5 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {it.createdAt ? new Date(it.createdAt).toLocaleString('fa-IR') : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box>
                        <Typography variant="body2">{it.phone || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">{it.userRole || '-'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{it.ip || '-'}</TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={0.5}>
                        <Chip label={it.action} size="small" variant="outlined" sx={{ borderRadius: 1 }} />
                        <Chip label={it.method} size="small" sx={{ borderRadius: 1, bgcolor: it.method === 'DELETE' ? 'error.lighter' : 'info.lighter' }} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="primary" fontWeight={500}>{it.resource}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {it.path}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                     <Chip 
                        label={it.statusCode || (it.success === false ? 'Error' : '200')} 
                        size="small" 
                        sx={{ 
                            bgcolor: it.success === false ? 'error.lighter' : 'success.lighter', 
                            color: it.success === false ? 'error.dark' : 'success.dark',
                            fontWeight: 'bold'
                        }}
                     />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" color="primary" onClick={() => setSelected(it)}>
                      <Info fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {items.length > 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={4} gap={2}>
            <IconButton 
                disabled={page <= 1} 
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
            >
                <ChevronRight fontSize="small" />
            </IconButton>
            <Typography variant="body2" fontWeight="bold">
                صفحه {page} از {totalPages}
            </Typography>
            <IconButton 
                disabled={page >= totalPages} 
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
            >
                <ChevronLeft fontSize="small" />
            </IconButton>
        </Box>
      )}

      <Dialog 
        open={!!selected} 
        onClose={() => setSelected(null)} 
        fullWidth 
        maxWidth="md"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">جزئیات لاگ</Typography>
            <IconButton onClick={() => setSelected(null)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <pre style={{ 
            whiteSpace: 'pre-wrap', 
            direction: 'ltr', 
            backgroundColor: '#1e293b', 
            color: '#f8fafc',
            padding: '16px',
            borderRadius: '12px',
            fontSize: isMobile ? '12px' : '14px',
            fontFamily: 'monospace',
            overflowX: 'auto'
          }}>
            {selected ? JSON.stringify(selected, null, 2) : ''}
          </pre>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default AuditLogs;
